import { Box, Typography, Divider } from "@mui/material";
import { AsyncButton } from "src/components/common/buttons/AsyncButton";
import {
	getAddableTreeItemSubConfiguratorComponentsForBulkSelection,
	TransformedSubConfiguratorOrListComponent,
} from "./treeItemModel/configuratorTreeItemQueries";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton";
import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AddSubConfiguratorsToTreeViewProps } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/AddSubConfiguratorsToTreeView.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField";

// Interface for selected sub-configurator with unique ID for tracking
interface SelectedSubConfigurator {
	id: string;
	component: TransformedSubConfiguratorOrListComponent;
}

export const AddSubConfiguratorsToTreeViewBulk = ({
	treeItem,
	closeDialog,
	onSubmit,
}: AddSubConfiguratorsToTreeViewProps) => {
	const [selectedSubConfigurators, setSelectedSubConfigurators] = useState<SelectedSubConfigurator[]>([]);

	const usableSubConfiguratorComponents = getAddableTreeItemSubConfiguratorComponentsForBulkSelection(
		treeItem,
		selectedSubConfigurators.map((item) => item.component),
	);

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<HorizontalBox sx={{ flex: 1, gap: 2 }}>
				<AvailableComponentsList
					components={usableSubConfiguratorComponents}
					onAddComponent={handleAddComponent}
				/>

				<Divider
					orientation="vertical"
					flexItem
					sx={{
						borderColor: "grey.300",
						mx: 1,
					}}
				/>

				<SelectedComponentsList
					selectedComponents={selectedSubConfigurators}
					onRemoveComponent={handleRemoveComponent}
				/>
			</HorizontalBox>

			<ActionButtons
				selectedCount={selectedSubConfigurators.length}
				onCancel={closeDialog}
				onSubmit={handleSubmit}
			/>
		</VerticalBox>
	);

	function handleAddComponent(component: TransformedSubConfiguratorOrListComponent) {
		const newId = crypto.randomUUID();
		setSelectedSubConfigurators((prev) => [...prev, { id: newId, component }]);
	}

	function handleRemoveComponent(id: string) {
		setSelectedSubConfigurators((prev) => prev.filter((item) => item.id !== id));
	}

	async function handleSubmit() {
		const components = selectedSubConfigurators.map((item) => item.component);
		await onSubmit(components);
	}
};

interface AvailableComponentsListProps {
	components: TransformedSubConfiguratorOrListComponent[];
	onAddComponent: (component: TransformedSubConfiguratorOrListComponent) => void;
}

const AvailableComponentsList = ({ components, onAddComponent }: AvailableComponentsListProps) => {
	const { t } = useTranslation();
	const [filterText, setFilterText] = useState("");

	const filteredComponents = components.filter((component) =>
		component.label.toLowerCase().includes(filterText.toLowerCase())
	);

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: 600 }}>
				{t("options")}
			</Typography>
			<AavoTextField
				label={t("search")}
				value={filterText}
				onChange={(value) => setFilterText(value)}
				sx={{ mb: 2 }}
				size="small"
			/>
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					border: "1px solid",
					borderColor: "grey.300",
					borderRadius: 2,
					backgroundColor: "grey.50",
					"&:hover": {
						borderColor: "grey.400",
					},
				}}
			>
				{filteredComponents.length === 0 ?
					<Box sx={{ p: 3, textAlign: "center" }}>
						<Typography variant="body2" color="text.secondary">
							{filterText ? t("no_results") : t("no_sub_configurations")}
						</Typography>
					</Box>
				:	filteredComponents.map((component) => (
						<HorizontalBox
							key={component.configurationComponentId}
							sx={{
								alignItems: "center",
								justifyContent: "space-between",
								padding: 2,
								margin: 1,
								borderRadius: 1,
								backgroundColor: "white",
								border: "1px solid",
								borderColor: "grey.200",
								transition: "all 0.2s ease-in-out",
								"&:hover": {
									borderColor: "primary.main",
									backgroundColor: "primary.50",
									transform: "translateY(-1px)",
									boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
								},
							}}
						>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{component.label}
							</Typography>
							<AavoIconButton
								size="small"
								icon={faPlus}
								onClick={() => onAddComponent(component)}
								sx={{
									color: "primary.main",
									"&:hover": {
										backgroundColor: "primary.100",
										transform: "scale(1.1)",
									},
								}}
							/>
						</HorizontalBox>
					))
				}
			</Box>
		</VerticalBox>
	);
};

interface SelectedComponentsListProps {
	selectedComponents: SelectedSubConfigurator[];
	onRemoveComponent: (id: string) => void;
}

const SelectedComponentsList = ({ selectedComponents, onRemoveComponent }: SelectedComponentsListProps) => {
	const { t } = useTranslation();

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<HorizontalBox sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
				<Typography variant="h6" sx={{ color: "text.primary", fontWeight: 600 }}>
					{t("selected_sub_configurators")}
				</Typography>
				{selectedComponents.length > 0 && (
					<Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
						{selectedComponents.length}
					</Typography>
				)}
			</HorizontalBox>
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					border: "1px solid",
					borderColor: "grey.300",
					borderRadius: 2,
					backgroundColor: "grey.50",
					minHeight: 0,
					"&:hover": {
						borderColor: "grey.400",
					},
				}}
			>
				{selectedComponents.length === 0 ?
					<Box sx={{ p: 4, textAlign: "center" }}>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
							{t("no_sub_configurations")}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t("select_or_add_sub_configuration")}
						</Typography>
					</Box>
				:	selectedComponents.map((selectedItem, index) => (
						<HorizontalBox
							key={selectedItem.id}
							sx={{
								alignItems: "center",
								justifyContent: "space-between",
								padding: 2,
								margin: 1,
								borderRadius: 1,
								backgroundColor: "white",
								border: "1px solid",
								borderColor: "success.200",
								transition: "all 0.2s ease-in-out",
								"&:hover": {
									borderColor: "error.main",
									backgroundColor: "error.50",
									transform: "translateY(-1px)",
									boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
								},
							}}
						>
							<HorizontalBox sx={{ alignItems: "center", gap: 1 }}>
								<Typography
									variant="body2"
									sx={{
										backgroundColor: "success.100",
										color: "success.800",
										px: 1,
										py: 0.5,
										borderRadius: 1,
										fontSize: "0.75rem",
										fontWeight: 600,
									}}
								>
									{index + 1}
								</Typography>
								<Typography variant="body1" sx={{ fontWeight: 500 }}>
									{selectedItem.component.label}
								</Typography>
							</HorizontalBox>
							<AavoIconButton
								size="small"
								icon={faTrash}
								onClick={() => onRemoveComponent(selectedItem.id)}
								sx={{
									color: "error.main",
									"&:hover": {
										backgroundColor: "error.100",
										transform: "scale(1.1)",
									},
								}}
							/>
						</HorizontalBox>
					))
				}
			</Box>
		</VerticalBox>
	);
};

interface ActionButtonsProps {
	selectedCount: number;
	onCancel: () => void;
	onSubmit: () => Promise<void>;
}

const ActionButtons = ({ selectedCount, onCancel, onSubmit }: ActionButtonsProps) => {
	const { t } = useTranslation();

	return (
		<HorizontalBox sx={{ mt: 3, justifyContent: "flex-end", gap: 2 }}>
			<AsyncButton variant="outlined" onClick={onCancel} color="secondary">
				{t("cancel")}
			</AsyncButton>
			<AsyncButton
				variant="contained"
				onClick={onSubmit}
				disabled={selectedCount === 0}
				sx={{
					minWidth: 120,
				}}
			>
				{selectedCount === 0 ? t("add") : `${t("add")} (${selectedCount})`}
			</AsyncButton>
		</HorizontalBox>
	);
};
